import { Link as RouterLink } from "react-router-dom";
import { Box, Button, TextField, MenuItem, Collapse } from "@mui/material";
import MonthIcon from "@mui/icons-material/CalendarMonth";
import { UserRole } from "features/auth/types/user";
import { Office } from "models/Office";
import Section from "components/Section";
import SectionHeader from "components/SectionHeader";
import OutlinedCard from "components/OutlinedCard";
import { useAuthContext } from "features/auth/AuthContext";
import { useFilterAllTimeEntriesPublicQuery, useFilterAllTimeEntriesQuery } from "app/api/absence";
import { addDays, startOfDay } from "date-fns";
import { useGetAllOfficesQuery } from "app/api/offices";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import LoadingDisplay from "components/LoadingDisplay";
import DataTable, { ColumnOptions } from "components/DataTable";
import { TimeEntry } from "models/TimeEntry";
import AbsenceTypeChip from "components/AbsenceTypeChip";
import { usePersistState } from "lib/hooks/usePersistState";

const columns: ColumnOptions<TimeEntry> = [
  {
    key: "userName",
    label: "Namn",
    renderCell: (row) => (
      <RouterLink to={`/users/view/${row.userId}`}>{row.userName || "Okänd"}</RouterLink>
    ),
  },
  {
    key: "type",
    label: "Typ",
    renderCell: (row) => <AbsenceTypeChip entry={row} />,
  },
  {
    key: "from",
    label: "Från",
    renderCell: (row) => row.from,
  },
  {
    key: "to",
    label: "Till",
    renderCell: (row) => row.to,
  },
];

const TodaysAbsentees: React.FC = () => {
  const { user: currentUser } = useAuthContext();
  const isAdminOrPayroll =
    currentUser?.role === UserRole.ADMIN || currentUser?.role === UserRole.PAYROLL;

  const startDate = startOfDay(new Date()).toISOString();
  const endDate = addDays(startOfDay(new Date()), 1).toISOString();

  const { data: offices } = useGetAllOfficesQuery();

  const [showContent, setShowContent] = usePersistState("TodaysAbsence.showContent", false);
  const [officeId, setOfficeId] = usePersistState<Office["id"] | null>(
    "TodaysAbsence.officeId",
    null
  );

  const queryHook = isAdminOrPayroll
    ? useFilterAllTimeEntriesQuery
    : useFilterAllTimeEntriesPublicQuery;

  const { data: todaysAbsentees, isFetching } = queryHook({
    searchQuery: "",
    fromDate: startDate,
    toDate: endDate,
    officeIds: officeId ? [officeId] : null,
    departmentIds: null,
    states: ["PENDING", "APPROVED"], // Exclude rejected
    types: [1, 2, 3, 4, 6, 7], // Exclude overtime (type 5)
    processed: null,
  });

  return (
    <Section>
      <SectionHeader title="Dagens frånvarande" />

      <OutlinedCard>
        <Collapse in={showContent || !isAdminOrPayroll}>
          <Box display="flex" justifyContent="start" alignItems="start" gap={1} pb={1}>
            <TextField
              select
              label="Kontor"
              size="small"
              variant="filled"
              value={officeId || ""}
              onChange={(e) => setOfficeId(parseInt(e.target.value) || null)}
              sx={{ width: 180, flexGrow: 0 }}
            >
              <MenuItem value="" sx={{ opacity: 0.33 }}>
                Alla
              </MenuItem>
              {offices?.map((office) => (
                <MenuItem key={office.id} value={office.id}>
                  {office.name}
                </MenuItem>
              ))}
            </TextField>

            <Button
              variant="text"
              component={RouterLink}
              to={isAdminOrPayroll ? "/reports?view=MONTHS" : "/absence?view=WORKWEEKS"}
              startIcon={<MonthIcon />}
              sx={{ ml: "auto" }}
            >
              Visa schema
            </Button>
          </Box>

          {isFetching ? (
            <LoadingDisplay message="Hämtar dagens frånvarande" />
          ) : (
            <DataTable
              columns={columns.filter((col) => isAdminOrPayroll || col.key !== "type")}
              data={todaysAbsentees ?? []}
              getKey={(row) => row.userId}
              striped
              noBottomBorder
              emptyMessage="Inga frånvarande idag"
              sx={{ mb: 1 }}
            />
          )}
        </Collapse>

        <Button
          variant="text"
          fullWidth
          hidden={!isAdminOrPayroll}
          startIcon={showContent ? <ExpandLess /> : <ExpandMore />}
          onClick={() => setShowContent(!showContent)}
          sx={{ justifyContent: "flex-end" }}
        >
          {showContent ? "Dölj frånvaro" : "Visa frånvaro"}
        </Button>
      </OutlinedCard>
    </Section>
  );
};

export default TodaysAbsentees;
